import React, { useContext, useEffect, useRef, useState } from 'react'
import { IconArrowDown } from '@s4l/icons/IconArrowDown'
import style from './index.module.css'
import Button from '../../Button'
import { cn } from '@s4l/utility-css'
import { FormContext } from '../context'

interface IFieldsetProps {
  textMore: string
  textLess: string
  children: React.ReactNode
}

interface Info {
  name: string
  trueValue: string
  multiple?: boolean
}

export const extractInfo = (children: React.ReactNode) => {
  const info: Info[] = []

  const traverse = (child: React.ReactNode) => {
    if (React.isValidElement(child)) {
      if (child.props?.name && typeof child.props.name === 'string') {
        info.push({
          name: child.props.name,
          trueValue: child.props.trueValue,
          multiple: child.props.multiple,
        })
      }

      if (child.props?.children) {
        React.Children.forEach(child.props.children, traverse)
      }
    }
  }

  React.Children.forEach(children, traverse)
  return info
}

const Collapsible = ({ textMore, textLess, children }: IFieldsetProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { form, hasFormInitialized } = useContext(FormContext)
  const [infoLazy, setInfoLazy] = useState<Info[]>([])
  const [info, setInfo] = useState<Info[]>([])

  const formJustMounted = useRef(false)

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const getInputsWithValues = () => {
    const inputs = extractInfo(children)

    const withValues = inputs.filter(input => {
      const formValue = form[input.name]
      if (input.multiple && Array.isArray(formValue)) {
        return formValue.some(inputValue => inputValue === input.trueValue)
      }
      return formValue !== undefined && (formValue === input.trueValue || Boolean(formValue))
    })

    return withValues
  }

  useEffect(() => {
    const withValues = getInputsWithValues()
    setInfoLazy(withValues)
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const withValues = getInputsWithValues()
    setInfo(withValues)
  }, [form]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const withValues = getInputsWithValues()
    setInfoLazy(withValues)
    setInfo(withValues)
    formJustMounted.current = true
  }, [hasFormInitialized]) // eslint-disable-line react-hooks/exhaustive-deps

  const allSelected = info.length === React.Children.toArray(children).length

  const childrenMapper = (child: React.ReactNode) => {
    const childInfo = extractInfo(child)[0]
    const selected = infoLazy.some(name => {
      if (name.multiple) {
        return name.name === childInfo.name && name.trueValue === childInfo.trueValue
      }

      return name.name === childInfo.name
    })
    return (
      <div
        key={childInfo.trueValue ?? childInfo.name}
        className={cn({
          [style.selected]: selected,
        })}
      >
        {child}
      </div>
    )
  }

  return (
    <div>
      <div className={cn(style.content, style.outside)}>
        {React.Children.toArray(children).map(childrenMapper)}
      </div>
      <div className={cn(style.collapsible, isOpen ? style.open : '')}>
        <div className={style.content}>{React.Children.toArray(children).map(childrenMapper)}</div>

        {!allSelected && (
          <Button
            className={['mt-8']}
            variant="text-flat"
            onClick={handleToggle}
            aria-expanded={isOpen}
            size="small"
            trailingIcon={<IconArrowDown size={16} />}
          >
            {isOpen ? textLess : textMore}
          </Button>
        )}
      </div>
    </div>
  )
}

Collapsible.displayName = 'Form.Collapsible'

export default Collapsible
